/**
 * Záložka Balíčky. Build jde do fleet katalogu, instalace ho stáhne odtamtud.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input, Label, Select } from '../../../components/ui';
import { callWidgetTool } from '../../../widgets/scope';
import type { EffectiveScope, WidgetRenderProps } from '../../../widgets/types';

type PortMode = 'acm' | 'ports' | 'websocket';

interface Account {
  entity_id: string;
  label: string;
  username: string;
}

interface Pkg {
  id: number;
  version: string;
  os: string;
  filename: string;
  sha256: string;
  size_bytes: number;
  port_mode: PortMode;
  connect_alias: string;
  current: boolean;
  brand: string;
  source_kind?: 'official' | 'own';
  source_url?: string;
  created_at?: string;
  can_configure: boolean;
  can_make_current: boolean;
  grants?: Account[];
}

interface ListResp {
  ok?: boolean;
  error?: string;
  can_grant?: boolean;
  versions?: string[];
  accounts?: Account[];
  defaults?: { source?: string; version?: string; port_mode?: PortMode; connect_alias?: string };
  addresses?: { host: string; name: string }[];
  packages?: Pkg[];
}

function scopeKey(scope: EffectiveScope): string {
  return `${scope.node ?? ''}:${scope.identity ?? ''}`;
}

function call<T>(tool: string, args: Record<string, unknown>, scope: EffectiveScope): Promise<T> {
  return callWidgetTool<T>(tool, args, scope, { silent: true });
}

function sizeOf(n: number): string {
  if (n >= 1048576) return `${(n / 1048576).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
}

function whenOf(value: string | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function accountText(account: Account): string {
  return account.username ? `${account.label} (${account.username})` : account.label;
}

function Fact({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className='min-w-0'>
      <div className='text-[11px] text-gray-500'>{label}</div>
      <div className={mono ? 'break-all font-mono text-xs' : 'truncate text-sm'}>{value}</div>
    </div>
  );
}

export function RustdeskPackagesWidget({ scope }: WidgetRenderProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [version, setVersion] = useState('1.5.0');
  const [portMode, setPortMode] = useState<PortMode>('acm');
  const [alias, setAlias] = useState('');
  const [source, setSource] = useState<'official' | 'own'>('official');
  const [sourceUrl, setSourceUrl] = useState('');
  const [picked, setPicked] = useState(false);
  const [grantFor, setGrantFor] = useState<number | null>(null);
  const [entityId, setEntityId] = useState('');
  const [accountQuery, setAccountQuery] = useState('');

  const listQ = useQuery({
    queryKey: ['rustdesk-packages', scopeKey(scope)],
    queryFn: () => call<ListResp>('rustdesk.package', { action: 'list' }, scope),
  });
  const versions = listQ.data?.versions && listQ.data.versions.length > 0 ? listQ.data.versions : ['1.5.0'];
  const addresses = listQ.data?.addresses ?? [{ host: '', name: '' }];
  const selectedVersion = versions.includes(version) ? version : versions[0];
  const selectedAlias = addresses.some((item) => item.host === alias) ? alias : '';
  useEffect(() => {
    const defaults = listQ.data?.defaults;
    if (!defaults || picked) return;
    if (defaults.version && versions.includes(defaults.version)) setVersion(defaults.version);
    if (defaults.port_mode === 'ports' || defaults.port_mode === 'websocket' || defaults.port_mode === 'acm') {
      setPortMode(defaults.port_mode);
    }
    if (typeof defaults.connect_alias === 'string') setAlias(defaults.connect_alias);
    if (defaults.source === 'own' || defaults.source === 'official') setSource(defaults.source);
    setPicked(true);
  }, [listQ.data, picked, versions]);
  const reload = () => void qc.invalidateQueries({ queryKey: ['rustdesk-packages'] });
  const done = (r: { ok?: boolean; error?: string } | undefined, okText: string) => {
    if (r && r.ok === false) {
      toast.error(r.error ?? t('rustdesk.packagesFailed'));
      return;
    }
    toast.success(okText);
    reload();
  };

  const buildMut = useMutation({
    mutationFn: () =>
      call<{ ok?: boolean; error?: string }>('rustdesk.package', {
        action: 'build',
        version: selectedVersion,
        port_mode: portMode,
        connect_alias: selectedAlias,
        source,
        source_url: source === 'own' ? sourceUrl.trim() : undefined,
        os: 'windows',
      }, scope),
    onSuccess: (r) => done(r, t('rustdesk.packagesBuilt')),
    onError: () => toast.error(t('rustdesk.packagesFailed')),
  });
  const modeMut = useMutation({
    mutationFn: (p: { id: number; port_mode: PortMode }) =>
      call<{ ok?: boolean; error?: string }>('rustdesk.package', { action: 'set_mode', ...p }, scope),
    onSuccess: (r) => done(r, t('rustdesk.packagesSaved')),
    onError: () => toast.error(t('rustdesk.packagesFailed')),
  });
  const aliasMut = useMutation({
    mutationFn: (p: { id: number; connect_alias: string }) =>
      call<{ ok?: boolean; error?: string }>('rustdesk.package', { action: 'set_alias', ...p }, scope),
    onSuccess: (r) => done(r, t('rustdesk.packagesSaved')),
    onError: () => toast.error(t('rustdesk.packagesFailed')),
  });
  const currentMut = useMutation({
    mutationFn: (id: number) =>
      call<{ ok?: boolean; error?: string }>('rustdesk.package', { action: 'set_current', id }, scope),
    onSuccess: (r) => done(r, t('rustdesk.packagesSaved')),
    onError: () => toast.error(t('rustdesk.packagesFailed')),
  });
  const grantMut = useMutation({
    mutationFn: (p: { action: 'grant' | 'revoke'; id: number; entity_id: string }) =>
      call<{ ok?: boolean; error?: string }>('rustdesk.package', p, scope),
    onSuccess: (r) => {
      setEntityId('');
      done(r, t('rustdesk.packagesSaved'));
    },
    onError: () => toast.error(t('rustdesk.packagesFailed')),
  });

  const data = listQ.data;
  const packages = data?.packages ?? [];
  const accounts = data?.accounts ?? [];
  const canGrant = data?.can_grant === true;
  const accountQueryKey = accountQuery.trim().toLowerCase();
  const modeLabel = (mode: PortMode) =>
    mode === 'ports' ? t('rustdesk.packagesModePorts') : mode === 'websocket' ? t('rustdesk.packagesModeWs') : t('rustdesk.packagesModeAcm');

  return (
    <Card className='m-1'>
      <CardHeader>
        <CardTitle className='text-sm'>{t('rustdesk.tabPackages')}</CardTitle>
      </CardHeader>
      <CardContent className='space-y-3'>
        <p className='text-xs text-gray-500'>{t('rustdesk.packagesHint')}</p>
        <div className='flex flex-wrap items-end gap-2'>
          <div>
            <Label>{t('rustdesk.packagesSourceKind')}</Label>
            <Select className='mt-1 w-36' size='sm' value={source} onChange={(e) => { setPicked(true); setSource(e.target.value === 'own' ? 'own' : 'official'); }}>
              <option value='official'>{t('rustdesk.packagesSourceOfficial')}</option>
              <option value='own'>{t('rustdesk.packagesSourceOwn')}</option>
            </Select>
          </div>
          <div>
            <Label>{t('rustdesk.packagesVersion')}</Label>
            <Select className='mt-1 w-32' size='sm' value={selectedVersion} onChange={(e) => { setPicked(true); setVersion(e.target.value); }}>
              {versions.map((item) => <option key={item} value={item}>{item}</option>)}
            </Select>
          </div>
          <div>
            <Label>{t('rustdesk.packagesMode')}</Label>
            <Select className='mt-1 w-44' size='sm' value={portMode} onChange={(e) => { setPicked(true); setPortMode(e.target.value as PortMode); }}>
              <option value='acm'>{modeLabel('acm')}</option>
              <option value='ports'>{modeLabel('ports')}</option>
              <option value='websocket'>{modeLabel('websocket')}</option>
            </Select>
          </div>
          <div>
            <Label>{t('rustdesk.packagesAlias')}</Label>
            <Select className='mt-1 w-56' size='sm' value={selectedAlias} onChange={(e) => { setPicked(true); setAlias(e.target.value); }}>
              {addresses.map((item) => (
                <option key={item.host || 'primary'} value={item.host}>
                  {item.host ? item.name : (item.name ? `${t('rustdesk.packagesAliasPrimary')} (${item.name})` : t('rustdesk.packagesAliasPrimary'))}
                </option>
              ))}
            </Select>
          </div>
          {source === 'own'
            ? (
              <div className='min-w-[16rem] flex-1'>
                <Label>{t('rustdesk.packagesSource')}</Label>
                <Input
                  className='mt-1 w-full'
                  size='sm'
                  value={sourceUrl}
                  placeholder={t('rustdesk.packagesSourceHint')}
                  onChange={(e) => setSourceUrl(e.target.value)}
                />
              </div>
            )
            : null}
          <Button size='sm' loading={buildMut.isPending} disabled={source === 'own' && !sourceUrl.trim()} onClick={() => buildMut.mutate()}>
            {t('rustdesk.packagesBuild')}
          </Button>
        </div>
        {listQ.isError
          ? <div className='text-xs text-gray-500'>{t('rustdesk.packagesFailed')}</div>
          : packages.length === 0
          ? <div className='text-xs text-gray-500'>{t('rustdesk.packagesEmpty')}</div>
          : (
            <div className='divide-y'>
              {packages.map((pkg) => (
                <div key={pkg.id} className='space-y-3 py-3 text-sm'>
                  <div className='flex flex-wrap items-center justify-between gap-2'>
                    <span className='truncate font-medium'>{pkg.brand} {pkg.version}</span>
                    <span className='flex items-center gap-2'>
                      {pkg.current ? <Badge variant='success'>{t('rustdesk.packagesCurrent')}</Badge> : null}
                      {pkg.can_make_current && !pkg.current
                        ? (
                          <Button size='sm' variant='secondary' disabled={currentMut.isPending} onClick={() => currentMut.mutate(pkg.id)}>
                            {t('rustdesk.packagesSetCurrent')}
                          </Button>
                        )
                        : null}
                    </span>
                  </div>
                  <div className='grid gap-3 sm:grid-cols-2'>
                    <Fact label={t('rustdesk.packagesFile')} value={pkg.filename} />
                    <Fact label={t('rustdesk.packagesVersion')} value={pkg.version} />
                    <Fact
                      label={t('rustdesk.packagesSourceKind')}
                      value={pkg.source_kind === 'own' ? t('rustdesk.packagesSourceOwn') : t('rustdesk.packagesSourceOfficial')}
                    />
                    <Fact label={t('rustdesk.packagesSize')} value={sizeOf(pkg.size_bytes)} />
                    <Fact label={t('rustdesk.packagesWhen')} value={whenOf(pkg.created_at)} />
                    <Fact label={t('rustdesk.packagesOs')} value={pkg.os} />
                  </div>
                  <Fact label={t('rustdesk.packagesSha')} value={pkg.sha256} mono />
                  {pkg.source_kind === 'own' && pkg.source_url
                    ? <div className='break-all text-[11px] text-gray-500'>{pkg.source_url}</div>
                    : null}
                  <div className='flex flex-wrap items-center gap-2'>
                    <Label>{t('rustdesk.packagesMode')}</Label>
                    <Select
                      size='sm'
                      value={pkg.port_mode}
                      disabled={!pkg.can_configure || modeMut.isPending}
                      title={pkg.can_configure ? undefined : t('rustdesk.packagesNoConfigure')}
                      onChange={(e) => modeMut.mutate({ id: pkg.id, port_mode: e.target.value as PortMode })}
                    >
                      <option value='acm'>{modeLabel('acm')}</option>
                      <option value='ports'>{modeLabel('ports')}</option>
                      <option value='websocket'>{modeLabel('websocket')}</option>
                    </Select>
                    <Label>{t('rustdesk.packagesAlias')}</Label>
                    <Select
                      size='sm'
                      value={addresses.some((item) => item.host === (pkg.connect_alias ?? '')) ? pkg.connect_alias : ''}
                      disabled={!pkg.can_configure || aliasMut.isPending}
                      title={pkg.can_configure ? undefined : t('rustdesk.packagesNoConfigure')}
                      onChange={(e) => aliasMut.mutate({ id: pkg.id, connect_alias: e.target.value })}
                    >
                      {addresses.map((item) => (
                        <option key={item.host || 'primary'} value={item.host}>
                          {item.host ? item.name : (item.name ? `${t('rustdesk.packagesAliasPrimary')} (${item.name})` : t('rustdesk.packagesAliasPrimary'))}
                        </option>
                      ))}
                    </Select>
                    {!pkg.can_configure ? <span className='text-[11px] text-gray-500'>{t('rustdesk.packagesNoConfigure')}</span> : null}
                  </div>
                  {canGrant
                    ? (
                      <div className='space-y-2'>
                        <div className='text-[11px] text-gray-500'>{t('rustdesk.packagesGrant')}</div>
                        {(pkg.grants ?? []).length === 0
                          ? <div className='text-xs text-gray-500'>{t('rustdesk.packagesGrantEmpty')}</div>
                          : (pkg.grants ?? []).map((account) => (
                            <div key={account.entity_id} className='flex items-center gap-2'>
                              <span>{accountText(account)}</span>
                              <Button
                                size='sm'
                                variant='ghost'
                                disabled={grantMut.isPending}
                                onClick={() => grantMut.mutate({ action: 'revoke', id: pkg.id, entity_id: account.entity_id })}
                              >
                                {t('rustdesk.packagesGrantRemove')}
                              </Button>
                            </div>
                          ))}
                        {grantFor === pkg.id
                          ? (
                            <div className='flex flex-wrap items-end gap-2'>
                              <div>
                                <Label>{t('rustdesk.packagesEntity')}</Label>
                                <Input
                                  className='mt-1 w-44'
                                  size='sm'
                                  value={accountQuery}
                                  placeholder={t('rustdesk.packagesAccountFind')}
                                  onChange={(e) => setAccountQuery(e.target.value)}
                                />
                              </div>
                              <Select className='mt-1 w-72' size='sm' value={entityId} onChange={(e) => setEntityId(e.target.value)}>
                                <option value=''>{t('rustdesk.packagesAccountPick')}</option>
                                {accounts
                                  .filter((account) => !(pkg.grants ?? []).some((grant) => grant.entity_id === account.entity_id))
                                  .filter((account) => {
                                    if (!accountQueryKey) return true;
                                    return accountText(account).toLowerCase().includes(accountQueryKey);
                                  })
                                  .map((account) => (
                                    <option key={account.entity_id} value={account.entity_id}>{accountText(account)}</option>
                                  ))}
                              </Select>
                              <Button
                                size='sm'
                                variant='secondary'
                                disabled={!entityId || grantMut.isPending}
                                onClick={() => grantMut.mutate({ action: 'grant', id: pkg.id, entity_id: entityId })}
                              >
                                {t('rustdesk.packagesGrantAdd')}
                              </Button>
                            </div>
                          )
                          : (
                            <Button size='sm' variant='ghost' onClick={() => { setGrantFor(pkg.id); setEntityId(''); setAccountQuery(''); }}>
                              {t('rustdesk.packagesGrantAdd')}
                            </Button>
                          )}
                      </div>
                    )
                    : null}
                </div>
              ))}
            </div>
          )}
      </CardContent>
    </Card>
  );
}
